import type { IconName } from "@bundle/tokens";
import type { Ref } from "react";
import { AppleMark, GoogleMark } from "./BrandMarks.tsx";
import { Icon } from "./Icon.tsx";
import { cx } from "./tokens.ts";

export type ButtonProps = {
  label: string;
  onPress: () => void;
  variant?: "primary" | "secondary" | "ghost" | "glass" | "destructive" | "success";
  size?: "sm" | "md" | "lg";
  icon?: IconName;
  /**
   * Third-party sign-in button drawn to the provider's branding rules (its own logo and colours; `variant` and
   * `icon` are ignored). Google: white with the colour "G" (dark in dark mode). Apple (HIG): black with the white
   * logo in light mode, white with the black logo in dark mode, system font. Native: iOS asset `BrandGoogle` and
   * `ASAuthorizationAppleIDButton`; Android drawables `ic_brand_google` / `ic_brand_apple`.
   */
  brand?: "google" | "apple";
  loading?: boolean;
  disabled?: boolean;
  testID: string;
};

export function Button(p: ButtonProps & { ref?: Ref<HTMLButtonElement> } & Record<string, unknown>) {
  const { label, onPress, variant = "secondary", size = "md", icon, brand, loading, disabled, testID, ref, ...rest } = p;
  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      data-testid={testID}
      className={cx("b-button", brand === undefined ? `b-button-${variant}` : `b-button-brand-${brand}`, `b-button-${size}`, loading === true && "b-loading")}
      disabled={disabled === true || loading === true}
      aria-busy={loading === true ? true : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onPress();
        const onClick = rest.onClick;
        if (typeof onClick === "function") onClick(e);
      }}
    >
      {loading === true ? (
        <span className="b-spinner" aria-hidden />
      ) : brand === "google" ? (
        <GoogleMark />
      ) : brand === "apple" ? (
        <AppleMark />
      ) : icon === undefined ? null : (
        <Icon name={icon} size="icon.sm" />
      )}
      <span>{label}</span>
    </button>
  );
}
